import Minus from '@mds/icons/Minus';
import Plus from '@mds/icons/Plus';
import { themeVars } from '@mds/ui/theme/themeVars';
// eslint-disable-next-line @nx/enforce-module-boundaries -- internal utility library
import { CreateSlotsAndSlotProps, SlotProps, useSlot } from '@mds/use-slot';
import { IconButton, Stack, StackProps, styled } from '@mui/material';
import { ComponentType, ReactNode, useCallback, type MouseEvent } from 'react';
import { adjustValueByStep } from './adjustValueByStep';

const SquareIconButton = styled(IconButton)(({ theme }) => ({
  width: 24,
  height: 24,
  color: `rgba(${themeVars(theme).palette.text.secondaryChannel} / 0.7)`,
  '&:hover': {
    color: themeVars(theme).palette.text.secondary,
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 1px ${themeVars(theme).palette.border.focused}`,
  },
}));

const NumberInputStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'trend',
})<{ trend?: 'positive' | 'negative' | 'warning' | 'info' }>(({ theme }) => ({
  height: '100%',
  flexDirection: 'row',
  paddingInline: 4,
  alignItems: 'center',
  variants: [
    {
      props: { trend: 'positive' },
      style: {
        color: themeVars(theme).palette.success.text,
      },
    },
    {
      props: { trend: 'negative' },
      style: {
        color: themeVars(theme).palette.error.text,
      },
    },
    {
      props: { trend: 'warning' },
      style: {
        color: themeVars(theme).palette.warning.text,
      },
    },
    {
      props: { trend: 'info' },
      style: {
        color: themeVars(theme).palette.info.text,
      },
    },
  ],
}));

const ValueSpan = styled('span')(({ theme }) => ({
  flex: 1,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: theme.typography.label1.lineHeight,
  userSelect: 'text',
  '&:focus-visible': {
    outline: `1px solid ${themeVars(theme).palette.border.focused}`,
  },
}));

export interface StepInputCellSlots {
  root?: ComponentType<typeof NumberInputStack>;
  decrementButton?: ComponentType<typeof IconButton>;
  incrementButton?: ComponentType<typeof IconButton>;
}

type StepInputCellSlotsAndSlotProps = CreateSlotsAndSlotProps<
  StepInputCellSlots,
  {
    root: SlotProps<typeof NumberInputStack>;
    decrementButton: SlotProps<typeof IconButton>;
    incrementButton: SlotProps<typeof IconButton>;
  }
>;

export type StepInputCellOwnProps = {
  /**
   * Cell value (typically a formatted number)
   */
  children: ReactNode;
  /**
   * Value as a number
   */
  value: number | null | undefined;
  /**
   * Step (typically +1)
   *
   * Must be larger than 0.
   */
  step: number;
  /**
   * Change handler for increment and decrement buttons
   *
   * To implement custom logic for incrementing or decrementing the value, you ignore the
   * `newValue` argument and use `type` to determine how to increment or decrement the
   * value.
   */
  onChange: (event: MouseEvent<HTMLButtonElement>, newValue: number, type: 'increment' | 'decrement') => void;
  /**
   * Visual indicator for cell state:
   * - 'positive': green text (success)
   * - 'negative': red text (error)
   * - 'warning': yellow/orange text (warning)
   * - 'info': blue text (info)
   */
  trend?: 'positive' | 'negative' | 'warning' | 'info';
} & Pick<StackProps, 'className' | 'style' | 'sx'>;

const stopPropagation = (event: MouseEvent<HTMLButtonElement>) => {
  // Prevent the click event from propagating to the cell
  event.stopPropagation();
};

/**
 * Table cell component with increment and decrement buttons.
 *
 * Internally, big.js is used to handle decimal steps. It is an optional peer dependency -
 * the package needs to be installed only if you import StepInputCell.
 */
export const StepInputCell = ({
  children,
  value,
  trend,
  onChange,
  step = 1,
  slots,
  slotProps,
}: StepInputCellOwnProps & StepInputCellSlotsAndSlotProps) => {
  if (step <= 0 || step <= Number.EPSILON || !Number.isFinite(step)) {
    throw new Error('Step must be a positive number greater than 0');
  }

  const [RootSlot, rootProps] = useSlot('root', {
    elementType: NumberInputStack,
    externalForwardedProps: { slots, slotProps },
    ownerState: {},
  });
  const [DecrementButtonSlot, decrementButtonProps] = useSlot('decrementButton', {
    elementType: SquareIconButton,
    externalForwardedProps: { slots, slotProps },
    ownerState: {},
  });
  const [IncrementButtonSlot, incrementButtonProps] = useSlot('incrementButton', {
    elementType: SquareIconButton,
    externalForwardedProps: { slots, slotProps },
    ownerState: {},
  });

  const decrement = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Don't propagate to RootSlot's onClick handler
      event.stopPropagation();
      const newValue = adjustValueByStep({ value: value as number, step, direction: 'decrement' });
      onChange(event, newValue, 'decrement');
    },
    [onChange, value, step],
  );
  const increment = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const newValue = adjustValueByStep({ value: value as number, step, direction: 'increment' });
      onChange(event, newValue, 'increment');
    },
    [onChange, value, step],
  );

  if (!Number.isFinite(value)) {
    return value;
  }
  return (
    <RootSlot trend={trend} {...rootProps}>
      <DecrementButtonSlot
        aria-label={`Decrement by ${step}`}
        onClick={decrement}
        onDoubleClick={stopPropagation}
        {...decrementButtonProps}
      >
        <Minus fontSize="sm" />
      </DecrementButtonSlot>

      <ValueSpan aria-label={`${trend} change`}>{children}</ValueSpan>

      {/* Indicator for screen readers */}
      {trend && <span style={{ display: 'none' }}>(modified - {trend})</span>}

      <IncrementButtonSlot
        aria-label={`Increment by ${step}`}
        onClick={increment}
        onDoubleClick={stopPropagation}
        {...incrementButtonProps}
      >
        <Plus fontSize="sm" />
      </IncrementButtonSlot>
    </RootSlot>
  );
};
